import { useMemo } from 'react'

import { TagType } from '~/client/src/shared/enums/TagType'
import { ITag } from '~/client/src/shared/models/Tag'
import User from '~/client/src/shared/models/User'
import UserProject from '~/client/src/shared/models/UserProject'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import ProjectRolesStore from '~/client/src/shared/stores/domain/ProjectRoles.store'
import ProjectTeamsStore from '~/client/src/shared/stores/domain/ProjectTeams.store'
import ProjectTradesStore from '~/client/src/shared/stores/domain/ProjectTrades.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import InitialState from '~/client/src/shared/stores/InitialState'

export interface IUserMetadata {
  companyName?: string
  companyId?: string
  companyAvatarUrl?: string
  roleNames?: string[]
  teamNames?: string[]
  tradeNames?: string[]
  accountPosition?: string
  accountType?: string
}

export interface ICurrentUserInfo {
  id: string
  companyId?: string
}

export interface IFilterData {
  tagsByType: Partial<Record<TagType, ITag[]>>
  // Read when a filter is applied, so membership is never a mount-time snapshot.
  getUserIdsByTagId: () => Map<string, string[]>
}

export interface IUserListSelectorStores {
  projectMembersStore: ProjectMembersStore
  userProjectsStore: UserProjectsStore
  companiesStore: CompaniesStore
  state: InitialState
  projectRolesStore: ProjectRolesStore
  projectTeamsStore: ProjectTeamsStore
  projectTradesStore: ProjectTradesStore
  tagsStore: TagsStore
}

export function useUserListSelectorData(stores: IUserListSelectorStores): {
  users: User[]
  userMetadata: Map<string, IUserMetadata>
  currentUser: ICurrentUserInfo | undefined
  filterData: IFilterData
} {
  const {
    projectMembersStore,
    userProjectsStore,
    companiesStore,
    state,
    projectRolesStore,
    projectTeamsStore,
    projectTradesStore,
    tagsStore,
  } = stores

  const users = projectMembersStore?.list || []

  const userMetadata = useMemo(() => {
    const map = new Map<string, IUserMetadata>()
    for (const user of users) {
      const companyId = UserProject.getCompanyId(user, userProjectsStore)
      const company = companyId
        ? companiesStore.getCompanyById(companyId)
        : undefined
      const companyName = company?.formattedName || undefined

      const roleIds = UserProject.getRolesIds(user, userProjectsStore, true)
      const roleNames = roleIds
        ?.map(id => projectRolesStore?.getInstanceById(id)?.name)
        ?.filter(Boolean) as string[]

      const teamIds = UserProject.getTeamsIds(user, userProjectsStore)
      const teamNames = teamIds
        ?.map(id => projectTeamsStore?.getInstanceById(id)?.name)
        ?.filter(Boolean) as string[]

      const tradeIds = UserProject.getTradesIds(user, userProjectsStore)
      const tradeNames = tradeIds
        ?.map(id => projectTradesStore?.getInstanceById(id)?.name)
        ?.filter(Boolean) as string[]

      map.set(user.id, {
        companyName: companyName || undefined,
        companyId: companyId || undefined,
        companyAvatarUrl: company?.avatarUrl || undefined,
        roleNames,
        teamNames,
        tradeNames,
        accountPosition: user.accountPosition || undefined,
        accountType:
          UserProject.getAccessType(user, userProjectsStore) || undefined,
      })
    }
    return map
  }, [
    users,
    userProjectsStore,
    companiesStore,
    projectRolesStore,
    projectTeamsStore,
    projectTradesStore,
  ])

  const currentUser = useMemo((): ICurrentUserInfo | undefined => {
    const user = state?.user
    if (!user) return undefined

    const companyId = UserProject.getCompanyId(user, userProjectsStore)
    return { id: user.id, companyId: companyId || undefined }
  }, [state?.user, userProjectsStore])

  const filterData = useMemo((): IFilterData => {
    const tagsByType =
      tagsStore?.tagListsWithDefaultsTagsMap ||
      ({} as Partial<Record<TagType, ITag[]>>)

    // All members, the same set the picker lists (invited users included).
    const getUserIdsByTagId = () =>
      new Map(
        Object.entries(tagsStore?.allUsersByRelatedTagIdMap || {}).map(
          ([tagId, tagUsers]) => [tagId, tagUsers.map(user => user.id)],
        ),
      )

    return { tagsByType, getUserIdsByTagId }
  }, [tagsStore, tagsStore?.tagListsWithDefaultsTagsMap])

  return { users, userMetadata, currentUser, filterData }
}
